import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import axios from 'axios';
import { AlertCircle, AlertTriangle, Check, Clock, CreditCard, Info, Lock, ShieldCheck } from 'lucide-react';
import { Spinner } from '@/components/app/misc';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn, csrfToken, loadScript } from '@/lib/utils';
import { TrustCards } from '@/components/billing/parts';
import { formatString, maskCep, maskCpf, validaCPF } from '@/components/billing/masks';
import { CountrySelect } from '@/components/billing/country-select';
/** Mesmas URLs das telas antigas. */
const STRIPE_JS = 'https://js.stripe.com/v3';
const PAGARME_JS = 'https://assets.pagar.me/pagarme-js/4.5/pagarme.min.js';
const BRAND_LABEL = {
    visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', elo: 'Elo', hipercard: 'Hipercard', diners: 'Diners Club', discover: 'Discover', unknown: '',
};
/** Bandeiras mostradas em "Aceitamos". */
const ACCEPTED = ['visa', 'mastercard', 'elo', 'amex', 'hipercard'];
/** Ordem em que os erros aparecem (a mesma dos campos na tela). */
const FIELD_ORDER = ['stripeNumber', 'number', 'name', 'stripeExpiry', 'exp', 'stripeCvc', 'cvc', 'cpf', 'country', 'zip', 'street', 'state', 'neighborhood', 'city'];
/** Bandeira pelo início do número (só para a prévia; quem valida é o gateway). */
function detectBrand(number) {
    const n = number.replace(/\D/g, '');
    if (!n)
        return 'unknown';
    if (/^(4011|4312|4389|4514|4576|5041|5066|5090|6362|6363|650|6516|6550)/.test(n))
        return 'elo';
    if (/^(606282|3841)/.test(n))
        return 'hipercard';
    if (/^3[47]/.test(n))
        return 'amex';
    if (/^3(0[0-5]|[68])/.test(n))
        return 'diners';
    if (/^6(011|5)/.test(n))
        return 'discover';
    if (/^(5[1-5]|2[2-7])/.test(n))
        return 'mastercard';
    if (/^4/.test(n))
        return 'visa';
    return 'unknown';
}
/** Bandeira informada pelo Stripe Elements. */
function brandFromStripe(brand) {
    switch (brand) {
        case 'visa': return 'visa';
        case 'mastercard': return 'mastercard';
        case 'amex': return 'amex';
        case 'diners': return 'diners';
        case 'discover': return 'discover';
        default: return 'unknown';
    }
}
/** Agrupa os dígitos como no cartão físico (4-4-4-4, ou 4-6-5 na Amex). */
function maskCardNumber(raw) {
    const digits = raw.replace(/\D/g, '').slice(0, 19);
    if (/^3[47]/.test(digits)) {
        return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)].filter(Boolean).join(' ');
    }
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}
/** Logotipo simplificado da bandeira, desenhado em SVG (sem imagens externas). */
function BrandMark({ brand, className }) {
    if (brand === 'mastercard') {
        return (_jsxs("svg", { viewBox: "0 0 48 30", className: cn('h-7 w-11', className), "aria-label": "Mastercard", role: "img", children: [_jsx("circle", { cx: "18", cy: "15", r: "11", fill: "#eb001b" }), _jsx("circle", { cx: "30", cy: "15", r: "11", fill: "#f79e1b", fillOpacity: "0.92" })] }));
    }
    if (brand === 'visa') {
        return (_jsx("svg", { viewBox: "0 0 48 30", className: cn('h-7 w-11', className), "aria-label": "Visa", role: "img", children: _jsx("text", { x: "24", y: "21", textAnchor: "middle", fontFamily: "Arial, Helvetica, sans-serif", fontWeight: "700", fontStyle: "italic", fontSize: "17", fill: "currentColor", children: "VISA" }) }));
    }
    if (brand === 'amex') {
        return (_jsxs("svg", { viewBox: "0 0 48 30", className: cn('h-7 w-11', className), "aria-label": "American Express", role: "img", children: [_jsx("rect", { x: "2", y: "3", width: "44", height: "24", rx: "3", fill: "#2e77bc" }), _jsx("text", { x: "24", y: "19", textAnchor: "middle", fontFamily: "Arial, Helvetica, sans-serif", fontWeight: "700", fontSize: "10", fill: "#fff", children: "AMEX" })] }));
    }
    if (brand === 'elo') {
        return (_jsxs("svg", { viewBox: "0 0 48 30", className: cn('h-7 w-11', className), "aria-label": "Elo", role: "img", children: [_jsx("circle", { cx: "24", cy: "15", r: "11", fill: "#000" }), _jsx("path", { d: "M17 15a7 7 0 0 1 12-5l-3 2.5a3.6 3.6 0 0 0-5.4 2.5z", fill: "#ffcb05" }), _jsx("path", { d: "M31 15a7 7 0 0 1-12 5l3-2.5a3.6 3.6 0 0 0 5.4-2.5z", fill: "#00a4e0" }), _jsx("path", { d: "M24 10.5a4.5 4.5 0 0 1 4.3 3.2l-3.3.8a1.2 1.2 0 0 0-1-.6z", fill: "#ef4123" })] }));
    }
    if (brand === 'hipercard' || brand === 'diners' || brand === 'discover') {
        return (_jsxs("svg", { viewBox: "0 0 48 30", className: cn('h-7 w-11', className), "aria-label": BRAND_LABEL[brand], role: "img", children: [_jsx("rect", { x: "2", y: "3", width: "44", height: "24", rx: "3", fill: brand === 'hipercard' ? '#b3131b' : brand === 'discover' ? '#f58220' : '#0079be' }), _jsx("text", { x: "24", y: "18.5", textAnchor: "middle", fontFamily: "Arial, Helvetica, sans-serif", fontWeight: "700", fontSize: "7.5", fill: "#fff", children: brand === 'hipercard' ? 'Hipercard' : brand === 'discover' ? 'DISCOVER' : 'DINERS' })] }));
    }
    return _jsx(CreditCard, { className: cn('h-6 w-6 opacity-70', className), "aria-hidden": "true" });
}
/** Prévia do cartão que acompanha a digitação e vira para mostrar o verso no CVC. */
function CardPreview({ brand, number, name, mm, aa, cvc, hidden, back }) {
    const groups = hidden
        ? ['••••', '••••', '••••', '••••']
        : (number || '').split(' ').concat(['', '', '', '']).slice(0, brand === 'amex' ? 3 : 4).map((g, i) => (g ? g : brand === 'amex' && i === 1 ? '••••••' : brand === 'amex' && i === 2 ? '•••••' : '••••'));
    const face = 'absolute inset-0 overflow-hidden rounded-2xl text-white shadow-[0_18px_40px_-18px_rgba(140,82,255,.7)] ring-1 ring-white/10 [backface-visibility:hidden]';
    return (_jsx("div", { className: "[perspective:1000px]", "aria-hidden": "true", children: _jsxs("div", { className: cn('relative aspect-[1.586/1] w-full transition-transform duration-500 [transform-style:preserve-3d]', back && '[transform:rotateY(180deg)]'), children: [_jsx("div", { className: cn(face, 'bg-gradient-to-br from-[#2a1458] via-[#5b21d6] to-[#8c52ff] p-5'), children: _jsxs("div", { className: "flex h-full flex-col justify-between", children: [_jsxs("div", { className: "flex items-start justify-between", children: [_jsx("span", { className: "h-8 w-11 rounded-md bg-gradient-to-br from-amber-200 via-yellow-300 to-amber-500 shadow-inner" }), _jsx(BrandMark, { brand: brand, className: "text-white" })] }), _jsx("p", { className: "whitespace-nowrap font-mono text-[17px] tracking-[0.08em] tabular-nums", children: groups.join(' ') }), _jsxs("div", { className: "flex items-end justify-between gap-3 text-[11px] uppercase tracking-wider", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-white/60", children: "Titular" }), _jsx("p", { className: "truncate text-sm font-semibold", children: name || 'Nome no cartão' })] }), _jsxs("div", { className: "shrink-0 text-right", children: [_jsx("p", { className: "text-white/60", children: "Validade" }), _jsx("p", { className: "font-mono text-sm font-semibold tabular-nums", children: hidden ? 'MM/AA' : `${mm || 'MM'}/${aa || 'AA'}` })] })] })] }) }), _jsxs("div", { className: cn(face, 'flex flex-col gap-4 bg-gradient-to-br from-[#2a1458] to-[#5b21d6] pt-6 [transform:rotateY(180deg)]'), children: [_jsx("div", { className: "h-10 bg-zinc-900" }), _jsxs("div", { className: "flex items-center justify-end gap-2.5 px-5", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wider text-white/70", children: "CVC" }), _jsx("span", { className: "inline-flex h-8 min-w-[64px] items-center justify-end rounded-md bg-white px-2.5 font-mono font-semibold text-zinc-900", children: hidden ? '•••' : cvc ? cvc.replace(/./g, '•') : '•••' })] }), _jsx("span", { className: "px-5 text-[11px] text-white/70", children: "O c\u00F3digo fica no verso, perto da assinatura." })] })] }) }));
}
/** Título numerado de cada bloco do formulário. */
function StepTitle({ step, children }) {
    return (_jsxs("legend", { className: "mb-3.5 flex items-center gap-2.5 font-semibold", children: [_jsx("span", { className: "flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary", children: step }), children] }));
}
/** Rótulo, campo e o erro do campo logo abaixo. */
function FormField({ id, label, error, className, children }) {
    return (_jsxs("div", { className: cn('grid content-start gap-1.5', className), children: [_jsx("label", { htmlFor: id, className: "text-[13px] font-medium", children: label }), children, error ? (_jsx("p", { id: id ? `${id}-error` : undefined, className: "text-xs font-medium text-destructive", children: error })) : null] }));
}
function emptyValues(cpf) {
    return { cpf: maskCpf(cpf || ''), name: '', number: '', mm: '', aa: '', cvc: '', country: 'Brasil', zip: '', state: '', city: '', street: '', neighborhood: '' };
}
const EMPTY_ELEMENTS = { stripeNumber: { complete: false }, stripeExpiry: { complete: false }, stripeCvc: { complete: false } };
/**
 * Modal "Adicionar cartão". Mesma tokenização no navegador e mesma rota POST
 * cards.store das telas antigas (Pagar.me pela API pública de tokens; Stripe BR
 * e EUA pelo Stripe Elements).
 *
 * payAmount: aberto para pagar uma fatura (o botão fica "Salvar" e, depois de
 * salvo, o modal avisa que está pagando e fecha sozinho).
 * onSaved: chamado logo que o cartão é salvo (a tela recarrega ou cobra).
 */
export function AddCardDialog({ open, onOpenChange, config, onSaved, prefillGeo = false, payAmount = null, }) {
    const gateway = config.gateway;
    const supported = ['pagarme', 'stripe', 'stripe_usa'].includes(gateway);
    // Stripe (BR e EUA) usa Stripe Elements: o Stripe.js v3 não tokeniza número digitado em input nosso.
    const usesElements = gateway === 'stripe' || gateway === 'stripe_usa';
    const isUsa = gateway === 'stripe_usa';
    // CEP brasileiro preenche o endereço (ViaCEP); no Stripe EUA o endereço é digitado.
    const searchesZip = !isUsa;
    const [values, setValues] = React.useState(() => emptyValues(config.ownerCpf));
    const [errors, setErrors] = React.useState({});
    const [showErrors, setShowErrors] = React.useState(false);
    const [formError, setFormError] = React.useState(null);
    const [zipStatus, setZipStatus] = React.useState('idle');
    const [cardBack, setCardBack] = React.useState(false);
    const [saved, setSaved] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const busyRef = React.useRef(false);
    const valuesRef = React.useRef(values);
    valuesRef.current = values;
    // Stripe Elements: containers criados aqui e montados no useEffect.
    const numberRef = React.useRef(null);
    const expiryRef = React.useRef(null);
    const cvcRef = React.useRef(null);
    const stripeRef = React.useRef(null);
    const cardNumberElementRef = React.useRef(null);
    const [elements, setElements] = React.useState(EMPTY_ELEMENTS);
    const elementsRef = React.useRef(elements);
    elementsRef.current = elements;
    const [stripeBrand, setStripeBrand] = React.useState('unknown');
    const inputRefs = React.useRef({});
    const bind = (name) => (el) => {
        inputRefs.current[name] = el;
    };
    const setBusyState = (value) => {
        busyRef.current = value;
        setBusy(value);
    };
    // Ao abrir: limpa o formulário e carrega os scripts do meio de pagamento.
    React.useEffect(() => {
        if (!open)
            return;
        setValues(emptyValues(config.ownerCpf));
        setErrors({});
        setShowErrors(false);
        setFormError(null);
        setZipStatus('idle');
        setCardBack(false);
        setSaved(null);
        setBusyState(false);
        if (gateway === 'pagarme') {
            loadScript(PAGARME_JS).catch(() => undefined);
        }
        if (usesElements) {
            loadScript(STRIPE_JS).catch(() => setFormError('Não foi possível carregar o formulário seguro da Stripe. Recarregue a página e tente de novo.'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Busca de endereço pelo CEP (ViaCEP). A rua vem com ", " para a pessoa só completar o número.
    const searchAddress = React.useCallback((zipCode) => {
        if (zipCode.length !== 8) {
            setZipStatus('idle');
            return;
        }
        setZipStatus('loading');
        fetch(`https://viacep.com.br/ws/${zipCode}/json/`)
            .then((r) => r.json())
            .then((response) => {
            if (response && response.uf !== undefined) {
                setValues((v) => ({
                    ...v,
                    state: response.uf || '',
                    city: response.localidade || '',
                    street: response.logradouro ? `${response.logradouro}, ` : '',
                    neighborhood: response.bairro || '',
                }));
                setZipStatus('found');
                window.setTimeout(() => {
                    const street = inputRefs.current.street;
                    if (street && !response.logradouro)
                        street.focus();
                    else if (street) {
                        street.focus();
                        street.setSelectionRange(street.value.length, street.value.length);
                    }
                }, 50);
            }
            else {
                setZipStatus('notfound');
            }
        })
            .catch(() => setZipStatus('notfound'));
    }, []);
    // Pagamento de fatura sem cartão: tenta preencher o CEP pela localização (ipapi.co), como antes.
    React.useEffect(() => {
        if (!open || !prefillGeo || gateway !== 'pagarme')
            return;
        fetch('https://ipapi.co/json')
            .then((r) => r.json())
            .then((response) => {
            const digits = response && response.postal ? String(response.postal).replace(/\D/g, '') : '';
            // Chega depois que a pessoa já pode ter digitado o CEP: nunca sobrescreve.
            if (digits.length < 5 || valuesRef.current.zip)
                return;
            const zipCode = digits.padEnd(8, '0').slice(0, 8);
            setValues((v) => (v.zip ? v : { ...v, zip: formatString(zipCode, '#####-###') }));
            searchAddress(zipCode);
        })
            .catch((error) => console.log('Falha ao obter GEO: ', error));
    }, [open, prefillGeo, gateway, searchAddress]);
    // Monta os Stripe Elements quando o modal está aberto e desmonta ao fechar.
    React.useEffect(() => {
        if (!open || !usesElements)
            return;
        let cancelled = false;
        const mounted = [];
        loadScript(STRIPE_JS)
            .then(() => {
            if (cancelled || !window.Stripe || !numberRef.current || !expiryRef.current || !cvcRef.current)
                return;
            const stripe = window.Stripe(config.publicKey || '', { locale: 'pt-BR' });
            stripeRef.current = stripe;
            const els = stripe.elements();
            const color = getComputedStyle(numberRef.current).color || '#18181b';
            const elementStyle = {
                base: { color, fontSize: '16px', '::placeholder': { color: '#a1a1aa' }, fontFamily: 'inherit' },
                invalid: { color: '#dc2626', iconColor: '#dc2626' },
            };
            const cardNumber = els.create('cardNumber', { placeholder: '0000 0000 0000 0000', style: elementStyle });
            const cardExpiry = els.create('cardExpiry', { placeholder: 'MM/AA', style: elementStyle });
            const cardCvc = els.create('cardCvc', { placeholder: '3 ou 4 dígitos do verso', style: elementStyle });
            cardNumber.mount(numberRef.current);
            cardExpiry.mount(expiryRef.current);
            cardCvc.mount(cvcRef.current);
            mounted.push(cardNumber, cardExpiry, cardCvc);
            cardNumberElementRef.current = cardNumber;
            const track = (key) => (event) => {
                setElements((e) => { var _a; return ({ ...e, [key]: { complete: !!event.complete, error: (_a = event.error) === null || _a === void 0 ? void 0 : _a.message } }); });
                setErrors((e) => {
                    if (!e[key])
                        return e;
                    const next = { ...e };
                    delete next[key];
                    return next;
                });
            };
            cardNumber.on('change', (event) => {
                track('stripeNumber')(event);
                setStripeBrand(brandFromStripe(event.brand));
            });
            cardExpiry.on('change', track('stripeExpiry'));
            cardCvc.on('change', track('stripeCvc'));
            cardCvc.on('focus', () => setCardBack(true));
            cardCvc.on('blur', () => setCardBack(false));
            window.setTimeout(() => {
                try {
                    cardNumber.focus();
                }
                catch (e) {
                    /* ignora */
                }
            }, 60);
        })
            .catch(() => undefined);
        return () => {
            cancelled = true;
            mounted.forEach((el) => {
                try {
                    el.unmount();
                    el.destroy();
                }
                catch (e) {
                    /* ignora */
                }
            });
            cardNumberElementRef.current = null;
            setElements(EMPTY_ELEMENTS);
            setStripeBrand('unknown');
        };
    }, [open, usesElements, config.publicKey]);
    // ---------- Validação: todos os erros de uma vez, embaixo de cada campo ----------
    const validate = (v) => {
        const e = {};
        if (usesElements) {
            const el = elementsRef.current;
            if (!el.stripeNumber.complete)
                e.stripeNumber = el.stripeNumber.error || 'Confira o número do cartão.';
            if (!el.stripeExpiry.complete)
                e.stripeExpiry = el.stripeExpiry.error || 'Validade inválida. Use MM/AA.';
            if (!el.stripeCvc.complete)
                e.stripeCvc = el.stripeCvc.error || 'Digite os 3 ou 4 dígitos do verso.';
        }
        else {
            const number = v.number.replace(/\s/g, '').replace(/\+/g, '').trim();
            if (number.length < 12 || !/^\d+$/.test(number))
                e.number = 'Confira o número do cartão.';
            const mm = v.mm.trim();
            const aa = v.aa.trim();
            const thisYear = new Date().getFullYear() % 100;
            if (mm.length !== 2 || aa.length !== 2 || !/^\d+$/.test(mm + aa) || parseInt(mm, 10) < 1 || parseInt(mm, 10) > 12) {
                e.exp = 'Validade inválida. Use MM/AA.';
            }
            else if (parseInt(aa, 10) < thisYear) {
                e.exp = 'Este cartão está vencido.';
            }
            if (!/^\d{3,4}$/.test(v.cvc.trim()))
                e.cvc = 'Digite os 3 ou 4 dígitos do verso.';
        }
        if (v.name.trim().length < 4)
            e.name = 'Escreva o nome como está no cartão.';
        if (gateway === 'pagarme') {
            const cpf = v.cpf.replace(/\D/g, '');
            if (cpf.length !== 11)
                e.cpf = 'Informe o CPF do titular (11 dígitos).';
            else if (!validaCPF(cpf))
                e.cpf = 'CPF inválido. Confira os números.';
        }
        else if (!v.country || !v.country.trim()) {
            e.country = 'Selecione o país do cartão.';
        }
        const zipDigits = v.zip.replace(/\D/g, '');
        if (isUsa ? v.zip.trim().length < 2 : zipDigits.length !== 8)
            e.zip = isUsa ? 'Informe o CEP/ZIP.' : 'O CEP tem 8 dígitos.';
        if (v.state.trim().length !== 2)
            e.state = isUsa ? 'Ex.: NY' : 'Ex.: SP';
        if (v.city.trim().length < 2)
            e.city = 'Informe a cidade.';
        if (v.street.replace(/,\s*$/, '').trim().length < (gateway === 'pagarme' ? 5 : 3))
            e.street = 'Informe rua e número.';
        if (v.neighborhood.trim().length < (gateway === 'pagarme' ? 4 : 2))
            e.neighborhood = 'Informe o bairro.';
        return e;
    };
    // Depois da primeira tentativa, os erros acompanham a digitação.
    React.useEffect(() => {
        if (showErrors)
            setErrors(validate(values));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, elements, showErrors]);
    const set = (name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        setFormError(null);
    };
    // ---------- Envio para cards.store (mesmos campos do $('#payment_form').serialize()) ----------
    const formData = (cardToken, cardGateway) => {
        const v = valuesRef.current;
        const data = new URLSearchParams();
        data.append('_token', csrfToken());
        data.append('cardToken', cardToken);
        data.append('cardGateway', cardGateway);
        if (gateway === 'pagarme') {
            data.append('cpf_cnpj', v.cpf);
        }
        data.append('holder_name', v.name);
        if (gateway === 'pagarme') {
            data.append('card_exp_month', v.mm);
            data.append('card_exp_year', v.aa);
        }
        if (gateway !== 'pagarme') {
            data.append('country', v.country);
        }
        data.append('zip_code', v.zip);
        data.append('state', v.state);
        data.append('city', v.city);
        data.append('street', v.street.replace(/,\s*$/, ''));
        data.append('neighborhood', v.neighborhood);
        return data;
    };
    /** Erro do banco/gateway: fica no topo do modal, sem apagar o que foi digitado. */
    const fail = (message) => {
        setFormError(message);
        setBusyState(false);
    };
    const saveCard = async (cardToken, cardGateway) => {
        var _a, _b;
        try {
            const { data } = await axios.post(config.storeUrl, formData(cardToken, cardGateway), { headers: { Accept: 'application/json' } });
            if (!data || data.error) {
                fail(((data === null || data === void 0 ? void 0 : data.message) || 'Falha ao salvar cartão. Tente novamente.').replace(/^Falha ao adicionar cartão!\s*/, ''));
                return;
            }
            setBusyState(false);
            setSaved(data.card || { id: 0, lastDigits: '', brand: '' });
            onSaved(cardGateway, data.card);
            // Pagando uma fatura: mostra a confirmação e fecha; a página cuida da cobrança.
            if (payAmount)
                window.setTimeout(() => onOpenChange(false), 1600);
        }
        catch (error) {
            console.log('Falha salvar cartao | ' + error);
            const message = axios.isAxiosError(error) ? (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message : undefined;
            fail((message || 'Falha ao salvar cartão. Verifique os dados e tente novamente.').replace(/^Falha ao adicionar cartão!\s*/, ''));
        }
    };
    /**
     * Token Pagar.me pela API pública (mesma chamada do $.ajax antigo, sem cabeçalhos extras).
     * Devolve o token ou a mensagem do que está errado no cartão.
     */
    const tokenizePagarme = async () => {
        const v = valuesRef.current;
        const body = new URLSearchParams();
        body.append('type', 'card');
        body.append('card[holder_name]', v.name);
        body.append('card[number]', v.number.replace(/\s/g, '').replace(/\+/g, ''));
        body.append('card[cvv]', v.cvc);
        body.append('card[exp_month]', v.mm);
        body.append('card[exp_year]', v.aa);
        try {
            const response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(config.publicKey || '')}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body: body.toString(),
            });
            const json = await response.json().catch(() => null);
            if (response.ok && json && json.id)
                return { token: String(json.id) };
            console.log('Falha ao criar token | ' + response.status, json);
            // Ex.: { errors: { card: ['The number field is not a valid card number'] } }
            const fields = JSON.stringify((json && json.errors) || {}).toLowerCase();
            if (fields.includes('number'))
                return { error: 'O número do cartão está incorreto.', field: 'number' };
            if (fields.includes('exp'))
                return { error: 'A validade do cartão está incorreta.', field: 'exp' };
            if (fields.includes('cvv'))
                return { error: 'O código de segurança (CVC) está incorreto.', field: 'cvc' };
            if (fields.includes('holder'))
                return { error: 'Confira o nome impresso no cartão.', field: 'name' };
            return { error: 'Não foi possível validar o cartão. Confira número, validade e CVC.' };
        }
        catch (error) {
            console.log('Falha ao criar token | ' + error);
            return { error: 'Não conseguimos falar com o Pagar.me. Verifique sua conexão e tente de novo.' };
        }
    };
    const submitPagarme = async () => {
        var _a;
        setBusyState(true);
        const result = await tokenizePagarme();
        if ('error' in result) {
            if (result.field) {
                setErrors((e) => ({ ...e, [result.field]: result.error }));
                (_a = inputRefs.current[result.field]) === null || _a === void 0 ? void 0 : _a.focus();
            }
            fail(result.error);
            return;
        }
        await saveCard(result.token, 'pagarme');
    };
    /** Stripe BR e EUA: token do Stripe Elements e envio para cards.store. */
    const submitStripe = async () => {
        const stripe = stripeRef.current;
        const cardNumber = cardNumberElementRef.current;
        if (!stripe || !cardNumber) {
            setFormError('Aguarde o formulário do cartão carregar e tente de novo.');
            return;
        }
        setBusyState(true);
        const v = valuesRef.current;
        const ownerInfo = {
            name: v.name,
            address_zip: v.zip,
            address_state: v.state,
            address_city: v.city,
            address_line1: v.street.replace(/,\s*$/, ''),
            address_line2: v.neighborhood,
            address_country: v.country,
        };
        try {
            const result = await stripe.createToken(cardNumber, ownerInfo);
            if (result.error) {
                console.log('Falha ao criar token Stripe | ' + result.error.message);
                fail(result.error.message || 'Confira os dados do cartão.');
                return;
            }
            await saveCard(result.token.id, gateway);
        }
        catch (error) {
            console.log(error);
            fail('Falha ao adicionar cartão. Tente de novo.');
        }
    };
    const submit = () => {
        var _a, _b;
        if (busyRef.current)
            return;
        setFormError(null);
        const found = validate(valuesRef.current);
        setErrors(found);
        setShowErrors(true);
        const first = FIELD_ORDER.find((k) => found[k]);
        if (first) {
            // Sem endereço à vista ainda: abre os campos para a pessoa ver o que falta.
            if (['street', 'state', 'city', 'neighborhood'].includes(first) && zipStatus === 'idle')
                setZipStatus('notfound');
            const target = inputRefs.current[first];
            if (target)
                target.focus();
            else if (first === 'stripeNumber')
                (_b = (_a = cardNumberElementRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
            return;
        }
        if (gateway === 'pagarme')
            submitPagarme();
        else
            submitStripe();
    };
    const onZipChange = (raw) => {
        if (isUsa) {
            set('zip', raw.slice(0, 13));
            return;
        }
        const masked = maskCep(raw);
        set('zip', masked);
        searchAddress(masked.replace(/[.\s-]/g, ''));
    };
    // Validade digitada como "MM/AA" num campo só; o estado continua em mm e aa (o envio não mudou).
    // A barra só aparece quando há ano, para o backspace conseguir apagar o mês.
    const expiryText = values.aa ? `${values.mm}/${values.aa}` : values.mm;
    const onExpiryChange = (raw) => {
        const digits = raw.replace(/\D/g, '').slice(0, 4);
        setValues((v) => ({ ...v, mm: digits.slice(0, 2), aa: digits.slice(2, 4) }));
        setFormError(null);
    };
    const err = (name) => (showErrors ? errors[name] : undefined);
    const inputClass = (name) => cn('h-11', err(name) && 'border-destructive focus-visible:ring-destructive');
    const aria = (name, id) => (err(name) ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {});
    const elementBox = (name) => cn('flex h-11 w-full items-center rounded-md border border-input bg-transparent px-3 text-foreground shadow-sm [&>*]:w-full', err(name) && 'border-destructive');
    const brand = usesElements ? stripeBrand : detectBrand(values.number);
    const gatewayName = gateway === 'pagarme' ? 'Pagar.me' : 'Stripe';
    const viaGateway = gateway === 'pagarme' ? 'pelo Pagar.me' : 'pela Stripe';
    const showAddress = !searchesZip || zipStatus === 'found' || zipStatus === 'notfound';
    // O resumo conta só o que está na tela (o endereço aparece depois do CEP).
    const ADDRESS = ['street', 'state', 'city', 'neighborhood'];
    const errorCount = Object.keys(errors).filter((k) => showAddress || !ADDRESS.includes(k)).length;
    return (_jsx(Dialog, { open: open, onOpenChange: (value) => !busy && onOpenChange(value), children: _jsx(DialogContent, { className: "max-h-[92vh] overflow-y-auto p-0 sm:max-w-[940px]", children: !supported ? (_jsxs("div", { className: "grid gap-5 p-6", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Adicionar cart\u00E3o de cr\u00E9dito" }), _jsx(DialogDescription, { children: "Os dados do cart\u00E3o s\u00E3o enviados direto para o processador de pagamentos." })] }), _jsxs(Alert, { children: [_jsx(Info, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "O cadastro de cart\u00E3o n\u00E3o est\u00E1 dispon\u00EDvel para o meio de pagamento da sua conta. Se precisar de ajuda, fale com o suporte." })] }), _jsx(TrustCards, { className: "sm:grid-cols-2" }), _jsx(DialogFooter, { children: _jsx(Button, { type: "button", variant: "outline", onClick: () => onOpenChange(false), children: "Fechar" }) })] })) : saved ? (_jsxs("div", { className: "flex flex-col items-center gap-3 px-7 pb-12 pt-14 text-center", children: [_jsx("span", { className: "flex h-[72px] w-[72px] items-center justify-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", children: _jsx(Check, { className: "h-9 w-9", strokeWidth: 2.4, "aria-hidden": "true" }) }), _jsx(DialogTitle, { className: "mt-1.5 text-[22px] font-semibold", children: "Cart\u00E3o salvo" }), _jsxs(DialogDescription, { className: "max-w-[420px] text-sm text-muted-foreground", children: [saved.lastDigits ? `${BRAND_LABEL[brandFromStripe(saved.brand)] || (saved.brand ? saved.brand.charAt(0).toUpperCase() + saved.brand.slice(1) : 'Cartão')} •••• ${saved.lastDigits} cadastrado.` : 'Cartão cadastrado.', ' ', payAmount ? 'Já estamos usando ele para pagar sua fatura.' : 'Ele será usado automaticamente nas próximas cobranças.'] }), payAmount ? (_jsxs("span", { className: "mt-1.5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-2 font-semibold text-primary", children: [_jsx(Spinner, {}), "Pagando a fatura de ", payAmount, "\u2026"] })) : (_jsx(Button, { type: "button", size: "lg", className: "mt-2.5 h-11 rounded-xl px-6", onClick: () => onOpenChange(false), children: "Concluir" }))] })) : (_jsxs("form", { onSubmit: (e) => {
                    e.preventDefault();
                    submit();
                }, noValidate: true, children: [_jsxs(DialogHeader, { className: "border-b px-7 pb-4 pt-6 text-left", children: [_jsx(DialogTitle, { className: "text-xl", children: "Adicionar cart\u00E3o de cr\u00E9dito" }), _jsx(DialogDescription, { children: payAmount ? `Para pagar a fatura de ${payAmount} e as próximas cobranças.` : 'Ele será usado nas próximas cobranças da sua assinatura.' })] }), _jsxs("div", { className: "flex flex-wrap", children: [_jsxs("aside", { className: "order-first flex flex-[1_1_100%] flex-col gap-4 px-5 pt-5 md:order-last md:flex-[0_1_330px] md:pl-2 md:pr-7 md:pt-5", children: [_jsx("div", { className: "mx-auto w-full max-w-[300px] md:max-w-none", children: _jsx(CardPreview, { brand: brand, number: values.number, name: values.name, mm: values.mm, aa: values.aa, cvc: values.cvc, hidden: usesElements, back: cardBack }) }), _jsxs("div", { className: "hidden gap-2 md:grid", children: [_jsx("span", { className: "text-xs font-medium text-muted-foreground", children: "Aceitamos" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: ACCEPTED.map((b) => (_jsx("span", { className: cn('flex h-8 w-12 items-center justify-center rounded-md border bg-background text-foreground transition-opacity', brand !== 'unknown' && brand !== b && 'opacity-30'), children: _jsx(BrandMark, { brand: b, className: "h-5 w-9" }) }, b))) })] }), _jsx("ul", { className: "hidden gap-3 text-[13px] md:grid", children: [
                                            { icon: ShieldCheck, title: 'Nós não vemos seu cartão', text: `Os dados vão criptografados direto ${gateway === 'pagarme' ? 'para o' : 'para a'} ${gatewayName}.` },
                                            { icon: Clock, title: 'Cobrança só no vencimento', text: 'Salvar o cartão não gera cobrança.' },
                                            { icon: CreditCard, title: 'Troque quando quiser', text: 'Cadastre outro cartão e remova este depois.' },
                                        ].map(({ icon: Icon, title, text }) => (_jsxs("li", { className: "flex gap-2.5", children: [_jsx("span", { className: "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary", children: _jsx(Icon, { className: "h-4 w-4", "aria-hidden": "true" }) }), _jsxs("span", { children: [_jsx("span", { className: "block font-semibold", children: title }), _jsx("span", { className: "text-muted-foreground", children: text })] })] }, title))) })] }), _jsxs("div", { className: "grid min-w-0 flex-[1_1_520px] content-start gap-5 px-5 pb-2 pt-5 sm:px-7", children: [formError ? (_jsxs(Alert, { variant: "destructive", role: "alert", className: "rounded-xl", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertTitle, { children: "N\u00E3o foi poss\u00EDvel salvar este cart\u00E3o" }), _jsx(AlertDescription, { children: formError })] })) : showErrors && errorCount > 0 ? (_jsxs("div", { role: "alert", className: "flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-[13px] font-medium text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200", children: [_jsx(AlertTriangle, { className: "mt-px h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" }), errorCount === 1 ? 'Corrija o campo destacado para continuar.' : `Corrija os ${errorCount} campos destacados para continuar.`] })) : null, _jsxs("fieldset", { className: "grid gap-3.5", children: [_jsx(StepTitle, { step: 1, children: "Dados do cart\u00E3o" }), usesElements ? (_jsxs(_Fragment, { children: [_jsx(FormField, { label: "N\u00FAmero do cart\u00E3o", error: err('stripeNumber'), children: _jsxs("div", { className: "relative", children: [_jsx("div", { ref: numberRef, className: cn(elementBox('stripeNumber'), 'pr-14'), "aria-label": "N\u00FAmero do Cart\u00E3o" }), _jsx("span", { className: "pointer-events-none absolute inset-y-0 right-2 flex items-center text-foreground", children: _jsx(BrandMark, { brand: brand }) })] }) }), _jsx(FormField, { id: "card-name", label: "Nome impresso no cart\u00E3o", error: err('name'), children: _jsx(Input, { id: "card-name", ref: bind('name'), maxLength: 40, placeholder: "Como est\u00E1 escrito no cart\u00E3o", autoComplete: "cc-name", value: values.name, onChange: (e) => set('name', e.target.value.toUpperCase()), className: inputClass('name'), ...aria('name', 'card-name') }) }), _jsxs("div", { className: "grid grid-cols-2 gap-3.5", children: [_jsx(FormField, { label: "Validade", error: err('stripeExpiry'), children: _jsx("div", { ref: expiryRef, className: elementBox('stripeExpiry'), "aria-label": "Validade (MM/AA)" }) }), _jsx(FormField, { label: "C\u00F3digo de seguran\u00E7a", error: err('stripeCvc'), children: _jsx("div", { ref: cvcRef, className: elementBox('stripeCvc'), "aria-label": "C\u00F3digo de seguran\u00E7a (CVC)" }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(FormField, { id: "card-number", label: "N\u00FAmero do cart\u00E3o", error: err('number'), children: _jsxs("div", { className: "relative", children: [_jsx(Input, { id: "card-number", ref: bind('number'), inputMode: "numeric", autoComplete: "cc-number", placeholder: "0000 0000 0000 0000", value: values.number, onChange: (e) => set('number', maskCardNumber(e.target.value)), className: cn(inputClass('number'), 'pr-14 font-mono tracking-wider'), ...aria('number', 'card-number') }), _jsx("span", { className: "pointer-events-none absolute inset-y-0 right-2 flex items-center text-foreground", children: _jsx(BrandMark, { brand: brand }) })] }) }), _jsx(FormField, { id: "card-name", label: "Nome impresso no cart\u00E3o", error: err('name'), children: _jsx(Input, { id: "card-name", ref: bind('name'), maxLength: 40, placeholder: "Como est\u00E1 escrito no cart\u00E3o", autoComplete: "cc-name", value: values.name, onChange: (e) => set('name', e.target.value.toUpperCase()), className: inputClass('name'), ...aria('name', 'card-name') }) }), _jsxs("div", { className: "grid grid-cols-2 gap-3.5", children: [_jsx(FormField, { id: "card-expiry", label: "Validade", error: err('exp'), children: _jsx(Input, { id: "card-expiry", ref: bind('exp'), inputMode: "numeric", autoComplete: "cc-exp", maxLength: 5, placeholder: "MM/AA", value: expiryText, onChange: (e) => onExpiryChange(e.target.value), className: cn(inputClass('exp'), 'font-mono tracking-wider'), ...aria('exp', 'card-expiry') }) }), _jsx(FormField, { id: "card-cvc", label: "C\u00F3digo de seguran\u00E7a", error: err('cvc'), children: _jsx(Input, { id: "card-cvc", ref: bind('cvc'), inputMode: "numeric", pattern: "[0-9]*", maxLength: 4, placeholder: "3 ou 4 d\u00EDgitos do verso", autoComplete: "cc-csc", value: values.cvc, onChange: (e) => set('cvc', e.target.value.replace(/\D/g, '')), onFocus: () => setCardBack(true), onBlur: () => setCardBack(false), className: cn(inputClass('cvc'), 'font-mono'), ...aria('cvc', 'card-cvc') }) })] })] })), gateway === 'pagarme' ? (_jsx(FormField, { id: "card-cpf", label: "CPF do titular", error: err('cpf'), children: _jsx(Input, { id: "card-cpf", ref: bind('cpf'), inputMode: "numeric", maxLength: 20, placeholder: "000.000.000-00", value: values.cpf, onChange: (e) => set('cpf', maskCpf(e.target.value)), className: inputClass('cpf'), ...aria('cpf', 'card-cpf') }) })) : null] }), _jsxs("fieldset", { className: "grid gap-3.5", children: [_jsx(StepTitle, { step: 2, children: "Endere\u00E7o de cobran\u00E7a" }), gateway !== 'pagarme' ? (_jsx(FormField, { id: "card-country", label: "Pa\u00EDs do cart\u00E3o", error: err('country'), children: _jsx(CountrySelect, { id: "card-country", ref: bind('country'), value: values.country, onChange: (name) => set('country', name), invalid: !!err('country') }) })) : null, _jsxs("div", { className: "grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3.5", children: [_jsx(FormField, { id: "card-zip", label: isUsa ? 'CEP / ZIP' : 'CEP', error: err('zip'), children: _jsx(Input, { id: "card-zip", ref: bind('zip'), inputMode: isUsa ? 'text' : 'numeric', maxLength: isUsa ? 13 : 9, placeholder: isUsa ? '10001' : '00000-000', autoComplete: "postal-code", value: values.zip, onChange: (e) => onZipChange(e.target.value), className: inputClass('zip'), ...aria('zip', 'card-zip') }) }), searchesZip ? (_jsx("p", { className: "pt-[30px] text-[13px]", "aria-live": "polite", children: zipStatus === 'loading' ? (_jsxs("span", { className: "inline-flex items-center gap-2 text-muted-foreground", children: [_jsx(Spinner, { className: "h-3.5 w-3.5" }), "Buscando endere\u00E7o\u2026"] })) : zipStatus === 'found' ? (_jsxs("span", { className: "inline-flex items-start gap-1.5 font-medium text-emerald-700 dark:text-emerald-400", children: [_jsx(Check, { className: "mt-0.5 h-4 w-4 shrink-0", strokeWidth: 2.4 }), "Endere\u00E7o preenchido pelo CEP. Confira o n\u00FAmero."] })) : zipStatus === 'notfound' ? (_jsx("span", { className: "text-muted-foreground", children: "N\u00E3o achamos esse CEP. Preencha o endere\u00E7o abaixo." })) : (_jsx("span", { className: "text-muted-foreground", children: "Preenchemos o endere\u00E7o a partir do CEP." })) })) : null] }), showAddress ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)] gap-3.5", children: [_jsx(FormField, { id: "card-street", label: "Endere\u00E7o (rua e n\u00FAmero)", error: err('street'), children: _jsx(Input, { id: "card-street", ref: bind('street'), autoComplete: "address-line1", placeholder: "Rua e n\u00FAmero", value: values.street, onChange: (e) => set('street', e.target.value), className: inputClass('street'), ...aria('street', 'card-street') }) }), _jsx(FormField, { id: "card-state", label: "Estado", error: err('state'), children: _jsx(Input, { id: "card-state", ref: bind('state'), maxLength: isUsa ? 3 : 2, placeholder: isUsa ? 'NY' : 'SP', value: values.state, onChange: (e) => set('state', e.target.value.toUpperCase()), className: cn(inputClass('state'), 'uppercase'), ...aria('state', 'card-state') }) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-3.5", children: [_jsx(FormField, { id: "card-neighborhood", label: "Bairro", error: err('neighborhood'), children: _jsx(Input, { id: "card-neighborhood", ref: bind('neighborhood'), maxLength: 100, placeholder: "Bairro", value: values.neighborhood, onChange: (e) => set('neighborhood', e.target.value), className: inputClass('neighborhood'), ...aria('neighborhood', 'card-neighborhood') }) }), _jsx(FormField, { id: "card-city", label: "Cidade", error: err('city'), children: _jsx(Input, { id: "card-city", ref: bind('city'), maxLength: 50, autoComplete: "address-level2", placeholder: "Cidade", value: values.city, onChange: (e) => set('city', e.target.value), className: inputClass('city'), ...aria('city', 'card-city') }) })] })] })) : null] })] })] }), _jsxs(DialogFooter, { className: "sticky bottom-0 mt-4 flex-col gap-3 border-t bg-background px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7", children: [_jsxs("p", { className: "flex items-center justify-center gap-2 text-xs text-muted-foreground", children: [_jsx(Lock, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), "Conex\u00E3o segura \u00B7 processado ", viaGateway] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { type: "button", variant: "outline", size: "lg", className: "h-12 flex-1 rounded-xl sm:flex-none", disabled: busy, onClick: () => onOpenChange(false), children: "Cancelar" }), _jsxs(Button, { type: "submit", size: "lg", disabled: busy, className: "h-12 flex-[2] rounded-xl font-semibold shadow-[0_6px_16px_-6px_hsl(var(--primary)/0.55)] sm:min-w-[180px] sm:flex-none", children: [busy ? _jsx(Spinner, {}) : _jsx(Lock, { className: "h-4 w-4" }), busy ? 'Validando com o banco…' : payAmount ? 'Salvar' : 'Salvar cartão'] })] })] })] })) }) }));
}
